import { getHomeUrl, getCourseCatalogListUrl, getStaffRoutePath } from '@/utils/cp-context';
import type { Breadcrumb } from '@ps-refarch-ux/neon';
import { CourseStatus } from '@/types';
import { STAFF_ROUTES } from '@/constants/staff-routes';

/** Prevent navigation so current breadcrumb is unclickable. */
function preventBreadcrumbNav(e: Event): void {
  e.preventDefault();
  e.stopPropagation();
}

export enum EMPTY_COURSE_CATALOG_TEXT {
  Title = 'No courses',
  Body = 'Add or import courses to start building a catalog for students to explore in Naviance and plan their learning.',
  Button = 'Add course',
  NoFilterResultsTitle = 'No results found',
  NoFilterResultsBody = '',
}

export enum EMPTY_COURSE_CATALOG_ID {
  Button = 'no-course-catalog',
}

export enum COURSE_CATALOG_ID {
  Breadcrumbs = 'course-catalog-breadcrumbs',
  AddButton = 'add-course-button',
  ImportButton = 'import-courses-button',
  ExportButton = 'export-courses-button',
  FiltersButton = 'filters-button',
  FilterCancelButton = 'course-catalog-filter-close',
  FilterApplyButton = 'course-catalog-filter-apply',
  SearchInput = 'search-course-input',
  BulkActionsButton = 'bulk-actions-button',
  HideInactiveCheckbox = 'hide-inactive-checkbox',
}

export enum COURSE_CATALOG_TEXT {
  BreadcrumbHome = 'Home',
  Title = 'Course catalog',
  AddButton = 'Add course',
  /** Last breadcrumb on edit-course route (matches AddButton style). */
  EditCourseBreadcrumb = 'Edit course',
  ImportButton = 'Import courses',
  ExportButton = 'Export',
  /** NeonTooltip for the Export header button (district: catalog + mapping). */
  ExportButtonTooltip = 'Export the course catalog or course mappings as a CSV file.',
  /** NeonTooltip when only catalog export is available (school staff). */
  ExportButtonTooltipSchool = 'Export the course catalog as a CSV file.',
  FiltersButton = 'Filters',
  FilterApply = 'Apply',
  HideInactiveCourses = 'Hide inactive courses',
  SearchPlaceholder = 'Search course ID or name',
  BulkActionsButton = 'Bulk actions',
  CoursesCount = 'courses',
}

export enum COURSE_CATALOG_ICON {
  Add = 'add',
  Import = 'media-import',
  Export = 'caret-down',
  Filter = 'filter',
  /** Filters button icon (same as POS: settings-tune) */
  Filters = 'settings-tune',
  Search = 'search',
}

/** Default page size when course catalog loads. */
export const DEFAULT_COURSE_CATALOG_PAGE_SIZE = 25;

/** Options for "Show" dropdown: 25, 50, 100, 200. */
export const COURSE_CATALOG_PAGE_SIZE_OPTIONS = [25, 50, 100, 200] as const;

/** Show pagination bar only when total courses exceed this (e.g. 26+). */
export const COURSE_CATALOG_PAGINATION_THRESHOLD = 25;

/** Toast messages for export (info when export begins, error when it fails). */
export const EXPORT_TOAST = {
  ExportingCourseData: 'Exporting course data',
  ExportingCourseMapping: 'Exporting course mapping',
  UnableToExport: 'Unable to export',
} as const;

export const EXPORT_CATALOG_DOWNLOAD_FILENAME = 'Courses.output.csv';
export const EXPORT_MAPPING_DOWNLOAD_FILENAME = 'Mappings.output.csv';

/** Info toast stays until export completes (manual dismiss). Error uses API-aligned timeout. */
export const EXPORT_INFO_TOAST_OPTIONS = { dismissible: false } as const;
export const EXPORT_ERROR_TOAST_OPTIONS = { timeout: 30000 } as const;

export function getCourseCatalogBreadcrumbs(options?: {
  onBreadcrumbLeave?: () => void;
}): Breadcrumb[] {
  const leaveHook = options?.onBreadcrumbLeave
    ? { onUserClick: () => options.onBreadcrumbLeave?.() }
    : {};
  return [
    { text: COURSE_CATALOG_TEXT.BreadcrumbHome, url: getHomeUrl(), ...leaveHook },
    { text: COURSE_CATALOG_TEXT.Title, url: getCourseCatalogListUrl(), ...leaveHook },
  ];
}

/** Breadcrumbs for Add / Edit Course: Home > Course catalog > Add course | Edit course. */
export function getAddCourseBreadcrumbs(options?: {
  onBreadcrumbLeave?: () => void;
  /** When true, last segment is "Edit course" (edit route); otherwise "Add course". */
  isEditMode?: boolean;
}): Breadcrumb[] {
  const leaveHook = options?.onBreadcrumbLeave
    ? { onUserClick: () => options.onBreadcrumbLeave?.() }
    : {};
  const lastText = options?.isEditMode
    ? COURSE_CATALOG_TEXT.EditCourseBreadcrumb
    : COURSE_CATALOG_TEXT.AddButton;
  return [
    { text: COURSE_CATALOG_TEXT.BreadcrumbHome, url: getHomeUrl(), ...leaveHook },
    { text: COURSE_CATALOG_TEXT.Title, url: getCourseCatalogListUrl() },
    { text: lastText, url: '#', onUserClick: preventBreadcrumbNav },
  ];
}

/** Breadcrumbs for View Course: Home > Course catalog > {course name} (current page not a link). */
export function getViewCourseBreadcrumbs(
  courseName: string,
  options?: { student?: boolean }
): Breadcrumb[] {
  const catalogUrl = options?.student
    ? '/course-planner-next/catalog'
    : getStaffRoutePath(`/${STAFF_ROUTES.courseCatalog.list}`);

  /** Student app (`AppStudent`): same as Explore course catalog — staff `getHomeUrl()` targets /staff or Succeed dashboard and breaks the student shell. */
  const homeUrl = options?.student ? '/' : getHomeUrl();

  return [
    { text: COURSE_CATALOG_TEXT.BreadcrumbHome, url: homeUrl },
    {
      text: COURSE_CATALOG_TEXT.Title,
      url: catalogUrl,
    },
    { text: courseName, url: '#', onUserClick: preventBreadcrumbNav },
  ];
}

/** Add Course page title and labels (aligned with Figma Add Course design) */
/**
 * Add Course page title and labels — aligned with legacy course-planner-ui
 * (`packages/staff/locales/en/course-page.json` / `CourseForm`).
 */
export const ADD_COURSE_PAGE_TEXT = {
  Title: 'Add course',
  EditTitle: 'Edit course',
  SectionOverview: 'Overview',
  SectionDetails: 'Details',
  SectionCourseRelationships: 'Course relationships',
  CourseName: 'Course name',
  CourseId: 'Course ID',
  /** Legacy c-p-ui `courseForm.checkingCourseId` — shown while GET …/courses/:id/exists is in flight */
  CourseIdChecking: 'Checking Course ID…',
  /** Legacy c-p-ui `courseForm.courseIdAlreadyInUse` */
  CourseIdAlreadyInUse: 'Course ID already in use',
  /** Shown when the exists check fails (non-404); save stays available */
  CourseIdVerifyError: 'Unable to verify Course ID. You can still try to save.',
  StateId: 'State ID',
  SubjectArea: 'Subject area',
  AddSubjectArea: 'Add subject area',
  Credits: 'Credits',
  /** Matches legacy CreditsField additionalDescription */
  CreditsHelper: 'Enter a number such as 0.5, 2.5, or 5.',
  CourseDuration: 'Course duration',
  CourseDurationNone: 'None',
  /** Closed-state empty label when no duration is selected (staff mock). */
  CourseDurationPlaceholder: 'None',
  GradeLevels: 'Grade level',
  SelectAll: 'Select all',
  Status: 'Status',
  CourseDescription: 'Course description',
  InstructionalLevel: 'Instructional level',
  /** Closed-state empty label when no level is selected (staff mock). */
  InstructionalLevelPlaceholder: 'Select instructional level',
  CreditType: 'Credit type',
  /** Subheading above repeatable / CTE / teacher recommendation checkboxes (staff mock). */
  CourseAttributes: 'Course attributes',
  CanBeRepeatedForCredit: 'Can be repeated for credit',
  QualifiesAsCTECourse: 'Qualifies as a CTE course',
  /** Checkbox: checked = legacy Yes, unchecked = legacy No (default unchecked). */
  RequiresTeacherRecommendation: 'Requires teacher recommendation',
  CreditRecovery: 'Credit recovery courses',
  CreditRecoveryHelper: 'Choose courses that students may take to recover credit for this course.',
  EquivalentCourses: 'Equivalent courses',
  EquivalentHelper: 'Choose courses that students can substitute for this course.',
  PrerequisiteText: 'Prerequisite description',
  PrerequisiteTextHelper:
    'Describe the requirements students must complete before taking this course.',
  /** Shown when stored HTML exceeds the Neon max (e.g. legacy courses over the limit on edit). */
  PrerequisiteDescriptionTooLong: 'Prerequisite description is too long',
  CourseDescriptionTooLong: 'Course description is too long',
  CoursePrerequisites: 'Prerequisite courses',
  PrerequisiteHelper: 'Choose courses that students must complete before taking this course.',
  /** Shown as Neon accessible label for OR groups after the first (no visible section heading). */
  PrerequisiteAlternativeGroupLabel: 'Additional prerequisite group (OR)',
  /** Visual divider between prerequisite AND-groups (OR logic between groups). */
  PrerequisiteOrDivider: 'OR',
  AddOrCondition: 'Add an OR condition',
  /** Tooltip / short label for delete control on an added OR prerequisite group (not shown on the first group). */
  RemoveOrConditionTooltip: 'Remove',
  /** Accessible name for removing one alternative prerequisite (OR) group. */
  RemoveOrConditionAriaLabel: 'Remove this alternative prerequisite group',
  /** Shown between prerequisite course groups (OR logic). */
  PrerequisiteOrBetweenGroups: 'OR',
  /** Accessible name for additional prerequisite multiselect rows (no visible title). */
  PrerequisiteAlternativeSetLabel: 'Alternative prerequisite courses',
  CourseCorequisites: 'Co-requisite courses',
  /** Matches mandated-courses drawer `NeonSearch` placeholder. */
  SearchByCourseNameOrId: 'Search by course name or ID',
  Cancel: 'Cancel',
  Save: 'Save',
  SaveAndMapToSchools: 'Save and map to schools',
  /** Floating success toast — secondary action (Figma: outline “Map to schools”) */
  ToastMapToSchools: 'Map to schools',
} as const;

/** Max visible/plain-text length for course description (rich-text HTML markup is excluded). */
export const MAX_COURSE_DESCRIPTION_PLAIN_TEXT_LENGTH = 3000;

/** Max character count for the plain-text prerequisite description field (add/edit course). */
export const MAX_PREREQUISITE_DESCRIPTION_LENGTH = 1500;

/** Success toast heading after a course is created (“[Course name] added to catalog”). */
export function formatCourseAddedToCatalogToastHeading(courseName: string): string {
  const n = courseName.trim();
  return `${n.length > 0 ? n : 'Course'} added to catalog`;
}

/** View Course (read-only) — NAVI-39250 */
export const VIEW_COURSE_PAGE_TEXT = {
  BreadcrumbTitleFallback: 'View course',
  Close: 'Close',
  MapCourseToSchools: 'Map course to schools',
  Previous: 'Previous',
  EditCourse: 'Edit course',
  DeleteCourse: 'Delete course',
  SectionOverview: 'Overview',
  MappedSchools: 'Available at schools',
  NoSchoolsMapped: 'Course is not mapped to any schools.',
  SectionDetails: 'Details',
  SectionCourseRelationships: 'Course relationships',
  PrerequisiteFor: 'Prerequisite for',
  CourseDuration: 'Course duration',
  EmDash: '—',
  RelationshipEmpty: 'None',
  CourseBadgeLabel: 'Course',
  CoRequisiteHelper: 'Choose courses that must be taken alongside this course.',
  /** Student view course — relationship section subheadings (staff keeps ADD_COURSE_PAGE_TEXT helpers). */
  CreditRecoveryStudentHelper: 'Courses that count toward credit recovery.',
  EquivalentCoursesStudentHelper: 'Courses that count as an equivalent for this course.',
  CoursePrerequisitesStudentHelper: 'Courses that must be completed before taking this course.',
  PrerequisiteDescriptionStudentHelper: 'Details on the prerequisites for this course.',
  OrDivider: 'or',
} as const;

/**
 * Internal value for the "None" row in the course duration dropdown.
 * Choosing it clears the field so the closed control shows the placeholder again.
 */
export const COURSE_DURATION_OPTION_CLEAR = '__course_duration_clear__';

/**
 * Course duration codes align with import / PowerSchool-style values (parenthetical abbreviations).
 * Persisted on the course as `courseDuration` (API string code, e.g. YR, SM).
 */
export const COURSE_DURATION_SELECT_OPTIONS: ReadonlyArray<{ value: string; text: string }> = [
  { value: COURSE_DURATION_OPTION_CLEAR, text: ADD_COURSE_PAGE_TEXT.CourseDurationNone },
  { value: 'YR', text: 'Year long (YR)' },
  { value: 'SM', text: 'Semester (SM)' },
  { value: 'TR', text: 'Trimester (TR)' },
  { value: 'QT', text: 'Quarter (QT)' },
  { value: 'QM', text: 'Quinmester (QM)' },
  { value: 'TM', text: 'Twelve month (TM)' },
  { value: 'LS', text: 'Long session (LS)' },
  { value: 'IT', text: 'Intersession (IT)' },
  { value: 'ST', text: 'Summer Term (ST)' },
  { value: 'OT', text: 'Other (OT)' },
];

/** @deprecated Use COURSE_DURATION_SELECT_OPTIONS */
export const COURSE_DURATION_OPTIONS = COURSE_DURATION_SELECT_OPTIONS;

/**
 * Human-readable label for view mode; unknown codes pass through.
 */
export function getCourseDurationDisplayLabel(stored: string | undefined): string | undefined {
  const code = stored?.trim();
  if (!code) {
    return undefined;
  }
  const match = COURSE_DURATION_SELECT_OPTIONS.find((o) => o.value === code);
  return match?.text ?? code;
}

/**
 * Internal value for the "None" row in the instructional level dropdown.
 * Choosing it clears the field so the closed control shows the placeholder again.
 */
export const INSTRUCTIONAL_LEVEL_OPTION_CLEAR = '__instructional_level_clear__';

/**
 * Add/edit course instructional levels — alphabetical order with "None" first to clear.
 * Values are persisted on the course as `instructionalLevel` (API string).
 */
export const INSTRUCTIONAL_LEVEL_SELECT_OPTIONS: ReadonlyArray<{ value: string; text: string }> = [
  { value: INSTRUCTIONAL_LEVEL_OPTION_CLEAR, text: 'None' },
  { value: 'Advanced placement', text: 'Advanced placement' },
  { value: 'Basic', text: 'Basic' },
  { value: 'College level', text: 'College level' },
  { value: 'Dual enrollment', text: 'Dual enrollment' },
  { value: 'English learner', text: 'English learner' },
  { value: 'General', text: 'General' },
  {
    value: 'Gifted and talented/advanced academic',
    text: 'Gifted and talented/advanced academic',
  },
  { value: 'High school equivalent', text: 'High school equivalent' },
  { value: 'Honors level', text: 'Honors level' },
  { value: 'International baccalaureate', text: 'International baccalaureate' },
  { value: 'Remedial', text: 'Remedial' },
  { value: 'Students with disabilities', text: 'Students with disabilities' },
  { value: 'Untracked', text: 'Untracked' },
];

/** School mapping drawer */
export const SCHOOL_MAPPING_DRAWER_TEXT = {
  DrawerIntroText:
    'Copy the course-to-school mappings from an existing course or select the schools that should have access to this course.',
  RadioCopyFromCourse: 'Make available to the same schools as this existing course',
  RadioSelectSchools: 'Make available to these schools',
  SaveMapping: 'Save mapping',
  NoSchoolsMapped: 'This course has no school mapping.',
  HideSchools: 'Hide schools',
  SchoolsMappedLabel: 'Schools mapped to this course',
} as const;

/** Success toast: course name as heading, mapped school count in subheading  */
export function formatSchoolMappingSuccessToast(
  courseName: string,
  schoolCount: number
): { heading: string; subheading: string } {
  return {
    heading: courseName,
    subheading:
      schoolCount === 0
        ? 'No schools mapped.'
        : `Mapped to ${schoolCount} school${schoolCount === 1 ? '' : 's'}.`,
  };
}

export const COURSE_STATUS_OPTIONS = [
  { value: CourseStatus.ACTIVE, text: 'Active' },
  { value: CourseStatus.INACTIVE, text: 'Inactive' },
] as const;

export const GRADE_OPTIONS = [
  { value: '6', text: '6' },
  { value: '7', text: '7' },
  { value: '8', text: '8' },
  { value: '9', text: '9' },
  { value: '10', text: '10' },
  { value: '11', text: '11' },
  { value: '12', text: '12' },
] as const;

/** Grade level checkbox options — “Select all” then 6–12 (staff add-course mock) */
export const GRADE_LEVEL_CHECKBOX_OPTIONS = [
  { value: 'select-all', label: ADD_COURSE_PAGE_TEXT.SelectAll },
  ...GRADE_OPTIONS.map((o) => ({ value: o.value, label: o.value })),
] as const;

/** Drawer filter state shape for Course Catalog */
export interface CourseCatalogDrawerFilters {
  schools: string[];
  subjectAreas: string[];
  grades: string[];
}

export const DEFAULT_DRAWER_FILTERS: CourseCatalogDrawerFilters = {
  schools: [],
  subjectAreas: [],
  grades: [],
};

/** Session storage keys for filter persistence */
export const SESSION_KEY_SEARCH = 'cp-course-catalog-filters-search';
export const SESSION_KEY_HIDE_INACTIVE = 'cp-course-catalog-filters-hideInactive';
export const SESSION_KEY_DRAWER = 'cp-course-catalog-filters-drawer';
export const SESSION_KEY_STUDENT_EXPLORE_SEARCH = 'cp-student-explore-catalog-search';
export const SESSION_KEY_STUDENT_EXPLORE_SCHOOLS = 'cp-student-explore-catalog-schools';
export const SESSION_KEY_STUDENT_EXPLORE_SUBJECTS_DRAFT =
  'cp-student-explore-catalog-subjects-draft';
export const SESSION_KEY_STUDENT_EXPLORE_SUBJECTS_APPLIED =
  'cp-student-explore-catalog-subjects-applied';
export const SESSION_KEY_STUDENT_EXPLORE_GRADES_DRAFT = 'cp-student-explore-catalog-grades-draft';
export const SESSION_KEY_STUDENT_EXPLORE_GRADES_APPLIED =
  'cp-student-explore-catalog-grades-applied';
/** Stores the SUCCEED_PROFILE cookie value of the user whose filters are saved. Used to detect user switch. */
export const SESSION_KEY_USER_ID = 'cp-course-catalog-user-id';
export const SESSION_KEY_STUDENT_CATALOG_SESSION = 'cp-student-catalog-session';

/** Clears persisted course catalog filters so leaving via breadcrumbs does not keep in-progress filter work. */
export function clearCourseCatalogFilterSession(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY_SEARCH);
    sessionStorage.removeItem(SESSION_KEY_HIDE_INACTIVE);
    sessionStorage.removeItem(SESSION_KEY_DRAWER);
    sessionStorage.removeItem(SESSION_KEY_STUDENT_EXPLORE_SEARCH);
    sessionStorage.removeItem(SESSION_KEY_STUDENT_EXPLORE_SCHOOLS);
    sessionStorage.removeItem(SESSION_KEY_STUDENT_EXPLORE_SUBJECTS_DRAFT);
    sessionStorage.removeItem(SESSION_KEY_STUDENT_EXPLORE_SUBJECTS_APPLIED);
    sessionStorage.removeItem(SESSION_KEY_STUDENT_EXPLORE_GRADES_DRAFT);
    sessionStorage.removeItem(SESSION_KEY_STUDENT_EXPLORE_GRADES_APPLIED);
    sessionStorage.removeItem(SESSION_KEY_USER_ID);
    sessionStorage.removeItem('pos-list-filters');
  } catch {
    // ignore private mode / quota
  }
}

/** Reset only the student school selection when the host login or viewed student changes. */
export function clearStudentFiltersIfSessionChanged(sessionId: string): void {
  try {
    if (sessionStorage.getItem(SESSION_KEY_STUDENT_CATALOG_SESSION) !== sessionId) {
      sessionStorage.removeItem(SESSION_KEY_STUDENT_EXPLORE_SCHOOLS);
      sessionStorage.setItem(SESSION_KEY_STUDENT_CATALOG_SESSION, sessionId);
    }
  } catch (error) {
    console.warn('Unable to synchronize student catalog filters with the host session:', error);
  }
}

/**
 * Clears course catalog filters if the current Succeed profile differs from the last stored one.
 * Called on mount — since Succeed logout is a server-side redirect with no JS event,
 * this is the only reliable signal that a different user has logged in.
 */
export function clearFiltersIfUserChanged(userId: string): void {
  try {
    const stored = sessionStorage.getItem(SESSION_KEY_USER_ID);
    if (stored !== null && stored !== userId) {
      clearCourseCatalogFilterSession();
    }
    sessionStorage.setItem(SESSION_KEY_USER_ID, userId);
  } catch {
    // ignore private mode / quota
  }
}

/** AG Grid header class shared across all Course Catalog columns. */
export const COURSE_CATALOG_GRID_HEADER_CLASS = 'neon-data-grid-header course-catalog-header';

/** Non-sortable headers: same styling as sortable, without pointer cursor (see neon-data-grid.css). */
export const COURSE_CATALOG_GRID_HEADER_CLASS_STATIC = `${COURSE_CATALOG_GRID_HEADER_CLASS} neon-data-grid-header--static`;

// ─── Single-course delete modal ───────────────────────────────────────────────

/** Verbiage for the single-course Delete modal. */
export const DELETE_COURSE_MODAL_TEXT = {
  /** Combine with the course name: `${TitlePrefix} ${courseName}?` */
  TitlePrefix: 'Delete',
  Body1:
    'Deleting this course removes it from the course catalog for all schools and from all plans of study. If the course is included in a published plan of study, republish the plan for the change to appear in the published version.',
  Body2:
    'You can also make a course inactive. This keeps it in your records but removes it from your active course list.',
  Cancel: 'Cancel',
  Confirm: 'Delete course',
} as const;

/** Toast messages for single-course delete. */
export const DELETE_COURSE_TOAST = {
  /** Fallback when the course name is missing; see `formatCourseDeletedSuccessHeading`. */
  Success: 'Course deleted',
  Error: 'Unable to delete course',
} as const;

/** Success toast heading after deleting one course, e.g. `"Algebra deleted"`. */
export function formatCourseDeletedSuccessHeading(courseName: string): string {
  const trimmed = courseName.trim();
  return trimmed.length > 0 ? `${trimmed} deleted` : DELETE_COURSE_TOAST.Success;
}

// ─── Bulk actions ─────────────────────────────────────────────────────────────

/** Dropdown options for the Bulk actions menu (order matches Figma spec). */
export const BULK_ACTION_OPTIONS = [
  { value: 'delete', label: 'Delete courses' },
  { value: 'update-status', label: 'Update status' },
  { value: 'update-subject-area', label: 'Update subject area' },
] as const;

/** Verbiage for the Bulk delete courses modal. */
export const BULK_DELETE_MODAL_TEXT = {
  Title: 'Bulk delete courses?',
  Body1Singular:
    'Deleting this course removes it from the course catalog for all schools and from all plans of study. If the course is included in a published plan of study, republish the plan for the change to appear in the published version.',
  Body1Plural:
    'Deleting these [X] courses will remove them from the course catalog for all schools and from all plans of study. If these courses are included in a published plan of study, republish the plan for the change to appear in the published version.',
  Body2Singular:
    'You can also make a course inactive. This keeps it in your records but removes it from your active course list.',
  Body2Plural:
    'You can also make courses inactive. This keeps them in your records but removes them from your active course list.',
  getBody1(selectedCount: number): string {
    return selectedCount === 1
      ? this.Body1Singular
      : this.Body1Plural.replace('[X]', String(selectedCount));
  },
  getBody2(selectedCount: number): string {
    return selectedCount === 1 ? this.Body2Singular : this.Body2Plural;
  },
  Cancel: 'Cancel',
  Confirm: 'Delete courses',
} as const;

/** Verbiage for the Bulk update status modal. */
export const BULK_STATUS_MODAL_TEXT = {
  Title: 'Bulk update course status?',
  /** Replace [#] with the selected count. */
  Body: 'Change the status of the [#] selected courses to:',
  Cancel: 'Cancel',
  Confirm: 'Update status',
} as const;

/** Verbiage for the Bulk update subject area modal. */
export const BULK_SUBJECT_AREA_MODAL_TEXT = {
  Title: 'Bulk update subject area?',
  /** Replace [#] with the selected count. */
  Body: 'Change the subject area of the [#] selected courses to:',
  Placeholder: 'Select subject area',
  Cancel: 'Cancel',
  Confirm: 'Update subject area',
} as const;

/** Toast messages for all bulk actions (success + error). */
export const BULK_ACTION_TOAST = {
  DeleteSuccess: 'Courses deleted',
  DeleteError: 'Unable to delete courses',
  /** Append the status label (e.g. "Active" or "Inactive") when showing the toast. */
  StatusSuccessPrefix: 'Course statuses updated to',
  StatusError: 'Unable to update course statuses',
  SubjectAreaSuccess: 'Course subject areas updated',
  SubjectAreaError: 'Unable to update course subject areas',
} as const;
